<template>
  <div class="chemicals-toxprints-view">
    <v-data-table
      v-if="chemicals && filteredChemicals.length"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, -1],
      }"
      :hide-default-footer="!chemicals || filteredChemicals.length <= 10"
      :headers="headers"
      :items="filteredChemicals"
      :page.sync="_currentPage"
      :items-per-page.sync="_itemsPerPage"
    >
      <template #[`item.structure`]="{ item }">
        <div
          :id="`a-0-${chemicalId(item)}`"
          :class="{ 'selected-chemical-card': isSelectedChemical(item) }"
          style="display: inline-block; position: relative"
        >
          <button
            type="button"
            @click="onChemicalClick(item)"
            class="image-action-button"
          >
            <img
              :src="`${TOXPRINTS_URL}/render?smiles=${encodeURIComponent(item.smiles)}`"
              :alt="`Chemical structure of ${encodeURIComponent(item.smiles) || 'chemical'}`"
              style="width: 150px; height: 150px"
            />
            <v-icon
              v-if="isInDSSTox(item)"
              class="top-right"
              color="green"
              aria-label="Present in DSSTox database"
            >
              mdi-database-check
            </v-icon>
          </button>
        </div>
        <div
          v-if="showNames"
          style="font-size: smaller"
        >
          <div
            v-for="prop in ['cid', 'sid', 'casrn', 'name']"
            :key="`org-${prop}`"
            class="ml-1 mb-1 text-truncate-150"
          >
            <a
              v-if="['cid', 'sid', 'casrn'].includes(prop)"
              :href="`https://comptox.epa.gov/dashboard/dsstoxdb/results?search=${item[prop]}`"
              target="epa"
            >
              {{ item[prop] }}
            </a>
            <span v-else>
              {{ item[prop] }}
            </span>
          </div>
        </div>
        <ChemPopover
          :chemical="item"
          :target="`a-0-${chemicalId(item)}`"
          source="toxprints"
        />
      </template>

      <template #[`item.active`]="{ item }">
        <v-chip
          v-if="item.active === true"
          color="red"
          small
          :title="item.activity"
          v-b-tooltip
        />
        <v-chip
          v-else-if="item.active === false"
          color="hollow"
          small
          :title="item.activity"
          v-b-tooltip
        />
      </template>

      <template #[`item.labels`]="{ item }">
        <div
          v-for="(n_label, i) in bits2n_labels(item.bits)"
          :key="i"
          :data-highlight-id="n_label[0]"
          data-highlight-type="lbl"
          style="white-space: nowrap"
          class="shadered-on-hover"
          @mouseenter="highlight($event, 'txp', n_label[0], 'shadowed')"
          @mouseleave="highlight($event, 'txp', n_label[0], 'shadowed')"
          @click="$emit('navigate', 1, n_label[0])"
        >
          <span>{{ n_label[1] }}</span>
        </div>
      </template>

      <template #[`item.toxprints`]="{ item }">
        <a>
          <img
            v-for="(id, i) in bits2ordinals(item.bits)"
            :key="i"
            :data-highlight-id="id"
            data-highlight-type="txp"
            :src="`${TOXPRINTS_URL}/render?id=${id}&profile=${profile}`"
            width="150"
            :alt="`ToxPrint structure for ID ${id}`"
            class="shadowed-on-hover"
            @mouseenter="highlight($event, 'lbl', id, 'shadered')"
            @mouseleave="highlight($event, 'lbl', id, 'shadered')"
            @click="$emit('navigate', 1, id)"
          />
        </a>
      </template>
    </v-data-table>

    <div
      v-else-if="chemicals"
      class="mt-15 text-center"
    >
      {{ emptyStateMessage }}
    </div>
  </div>
</template>

<script>
import { TOXPRINTS_URL } from '@/main'

import ChemPopover from '@/components/chemical/ChemPopover/ChemPopover.vue'

export default {
  components: { ChemPopover },

  props: {
    chemicals: {
      type: Array,
      default: () => [],
    },
    hideEmpty: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    profile: {
      type: String,
      default: null,
    },
    selectedChemicalKey: {
      type: [String, Number],
      default: null,
    },
    showNames: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 30,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
  },

  data: () => ({
    baseHeaders: [
      { text: 'Structure', value: 'structure', sortable: false },
      { text: 'Labels', value: 'labels', sortable: false },
      { text: 'ToxPrints', value: 'toxprints', width: '70%', sortable: false },
    ],
    activityHeaders: [
      { text: 'Structure', value: 'structure', sortable: false },
      { text: 'Active', value: 'active' },
      { text: 'Labels', value: 'labels', sortable: false },
      { text: 'ToxPrints', value: 'toxprints', width: '70%', sortable: false },
    ],
  }),

  computed: {
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    headers() {
      return this.chemicals.find((r) => r.activity) ? this.activityHeaders : this.baseHeaders
    },
    _itemsPerPage: {
      get() {
        return this.itemsPerPage
      },
      set(value) {
        this.$emit('update:itemsPerPage', value)
      },
    },
    _currentPage: {
      get() {
        return this.currentPage
      },
      set(value) {
        this.$emit('update:currentPage', value)
      },
    },
    filteredChemicals() {
      return this.chemicals.filter((chemical) => !this.hideEmpty || this.bits2n_labels(chemical.bits).length > 0)
    },
    emptyStateMessage() {
      if (!this.chemicals.length) {
        return 'No data to display'
      }

      if (this.hideEmpty && !this.filteredChemicals.length) {
        return "No toxprints found. Toggle 'Show empty rows' in the appearance settings to view empty rows."
      }

      return 'No data to display'
    },
  },

  methods: {
    chemicalId(chemical) {
      return chemical?.id ?? chemical?.chemId ?? chemical?.sid ?? chemical?.key
    },
    isSelectedChemical(chemical) {
      return this.selectedChemicalKey != null && this.chemicalId(chemical) === this.selectedChemicalKey
    },
    onChemicalClick(chemical) {
      this.$emit('chemicalSelected', chemical)
    },
    isInDSSTox(item) {
      return item.cid || item.sid || item.name || item.casrn
    },
    highlight(event, type, highlightId, className) {
      let row = event.currentTarget?.closest('tr')
      let selector = `[data-highlight-type="${type}"][data-highlight-id="${highlightId}"]`
      let el = row?.querySelector(selector)

      if (el) {
        el.classList.toggle(className)
      }
    },
    bits2n_labels(bits) {
      let n_labels = []
      if (bits && this.labels) {
        for (let i = 0; i < bits.length; i++) {
          if (bits[i]) n_labels.push([i, this.labels[i]])
        }
      }
      return n_labels
    },
    bits2ordinals(bits) {
      let ordinals = []
      if (bits) {
        for (let i = 0; i < bits.length; i++) {
          if (bits[i]) ordinals.push(i)
        }
      }
      return ordinals
    },
  },
}
</script>

<style scoped>
.chemicals-toxprints-view {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

.v-data-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
}

::v-deep .v-data-table__wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

::v-deep .v-data-table__wrapper thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}

::v-deep .v-data-footer {
  flex: 0 0 auto;
}

.selected-chemical-card {
  border-radius: 8px;
  box-shadow: 0 0 0 3px rgba(24, 115, 205, 0.9);
  background: rgba(24, 115, 205, 0.08);
}

.image-action-button {
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-block;
  line-height: 0;
  padding: 0;
}

.image-action-button:focus-visible {
  outline: 2px solid #1873cd;
  outline-offset: 2px;
}
</style>
